/**
 * Image Gallery with Zoom Alpine Component
 * Provides zoomable image gallery with touch and mouse support
 *
 * Usage in Twig:
 * <div x-data="imageGalleryZoom"
 *      data-initial-image="/path/to/image.jpg"
 *      data-initial-alt="Image description"
 *      data-initial-title="Image title">
 *   ...
 * </div>
 */
export default function ImageGalleryZoom() {
  return {
    selectedImage: '',
    selectedAlt: '',
    selectedTitle: '',
    transitioning: false,
    isZoomed: false,
    zoomX: 0,
    zoomY: 0,
    startX: 0,
    startY: 0,

    init() {
      this.selectedImage = this.$el.dataset.initialImage || '';
      this.selectedAlt = this.$el.dataset.initialAlt || '';
      this.selectedTitle = this.$el.dataset.initialTitle || '';

      requestIdleCallback(() => {
        this.$el.querySelectorAll('[data-full-src]').forEach((thumb) => {
          const img = new Image();
          img.src = thumb.dataset.fullSrc;
        });
      });
    },

    selectImage(image, alt, title) {
      if (image === this.selectedImage) return;
      this.transitioning = true;

      const img = new Image();
      img.src = image;

      const swap = () => {
        this.selectedImage = image;
        this.selectedAlt = alt;
        this.selectedTitle = title;
        this.isZoomed = false;
        requestAnimationFrame(() => {
          this.transitioning = false;
        });
      };

      const fadeDuration = 150;
      if (img.complete) {
        setTimeout(swap, fadeDuration);
      } else {
        img.onload = () => setTimeout(swap, fadeDuration);
        img.onerror = () => setTimeout(swap, fadeDuration);
      }
    },

    zoom(event) {
      if (this.isZoomed) {
        const rect = event.currentTarget.getBoundingClientRect();
        this.zoomX = ((event.clientX - rect.left) / rect.width) * 100;
        this.zoomY = ((event.clientY - rect.top) / rect.height) * 100;
      }
    },

    toggleZoom(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.zoomX = ((event.clientX - rect.left) / rect.width) * 100;
      this.zoomY = ((event.clientY - rect.top) / rect.height) * 100;
      this.isZoomed = !this.isZoomed;
      document.body.style.overflow = this.isZoomed ? 'hidden' : '';
    },

    touchStart(event) {
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
      if (this.isZoomed) {
        document.body.style.overflow = 'hidden';
      }
    },

    touchMove(event) {
      if (this.isZoomed) {
        const rect = event.currentTarget.getBoundingClientRect();
        const deltaX = event.touches[0].clientX - this.startX;
        const deltaY = event.touches[0].clientY - this.startY;
        this.zoomX += (deltaX / rect.width) * 100;
        this.zoomY += (deltaY / rect.height) * 100;
        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
      }
    },

    touchEnd() {
      if (this.isZoomed) {
        document.body.style.overflow = '';
      }
    },

    mouseDown(event) {
      if (this.isZoomed) {
        this.startX = event.clientX;
        this.startY = event.clientY;
        document.body.style.overflow = 'hidden';
      }
    },

    mouseMove(event) {
      if (this.isZoomed && event.buttons === 1) {
        const rect = event.currentTarget.getBoundingClientRect();
        const deltaX = event.clientX - this.startX;
        const deltaY = event.clientY - this.startY;
        this.zoomX += (deltaX / rect.width) * 100;
        this.zoomY += (deltaY / rect.height) * 100;
        this.startX = event.clientX;
        this.startY = event.clientY;
      }
    },

    mouseUp() {
      if (this.isZoomed) {
        document.body.style.overflow = '';
      }
    },
  };
}
